import { Dispatch, SetStateAction, useEffect, useState } from "react";
import ModalV2 from "../modal-v2";
import { Box, Divider, Stack, Typography } from "@mui/material";
import SwitchButton from "@/components/switch";
import { HeadingText, StyledBox, StyledTextInput } from "./styles";
import { clientInitialPermissions, preview_image } from "@/constants";
import { useGetTenantAllowedPagesQuery, useSetUserAllowedPagesMutation } from "@/graphql/page-access";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import PictureUploader from "@/components/picture-uploader";

import { MultiCheckbox, PartnerPicker } from "@/components";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useSnackbar, useUploadFile } from "@/hooks";
import { ValidationPatterns } from "@/utils/form-validation";
import {
  useLazyGetAccessSignedUrlQuery,
  useUpdateClientMutation,
} from "@/services";
// legacy DocumentAccessLevel removed

type UpdateUserModalProps = {
  show: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
  selectedPartner: SPOwnerPartnerTypes | null;
  setSelectedPartner: Dispatch<SetStateAction<SPOwnerPartnerTypes | null>>;
  clientDetails: Client;
  setClientDetails: Dispatch<SetStateAction<Client>>;
};

// legacy arrays removed

const UpdatePartnerModal = ({
  show,
  setShow,
  selectedPartner,
  setSelectedPartner,
  clientDetails,
}: UpdateUserModalProps) => {
  const { _id, firstName, profileImage, email, assignedPartner } =
    clientDetails;

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<ClientFormValues>({
    defaultValues: {
      firstName,
      email,
      profileImage: null,
      assignedPartner,
    },
  });

  const { isLoading: profileImgLoading, uploadFile } = useUploadFile();
  const [getAccessSignedUrl, { isFetching: isFetchingImageUrl }] =
    useLazyGetAccessSignedUrlQuery();

  const { show: showSnackBar } = useSnackbar();

  const [profilePreview, setProfilePreview] = useState<string | undefined>(
    undefined
  );
  const isDefaultImage = profilePreview === preview_image;

  const imageType = isDefaultImage ? "default" : "rounded";

  const [updateClient, { isLoading }] = useUpdateClientMutation();
  // legacy documents state removed
  const [allowedPages, setAllowedPages] = useState<string[]>(clientDetails.allowedPages || []);
  const [setUserAllowedPages] = useSetUserAllowedPagesMutation();
  const { selectedTenant } = useAppSelector((s) => s.tenantManagement);
  const user = useAppSelector((s) => s.auth.user);
  const isTDAdminSP =
    user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "SERVICE_PROVIDER";
  const { data: tenantPagesResp } = useGetTenantAllowedPagesQuery(
    isTDAdminSP ? { tenantId: selectedTenant?._id } : undefined
  );
  const PAGE_OPTIONS: Array<{ key: string; label: string }> = [
    { key: "ANALYTICS", label: "Analytics" },
    { key: "POTENTIAL_SAVINGS", label: "Potential Savings" },
    { key: "SCENARIO_MODELING", label: "Scenario Modeling" },
    { key: "CHAT_ASSISTANCE", label: "Chat Assistance" },
    { key: "PRODUCT_HUB", label: "Product Hub" },
    { key: "TRADE_DATA", label: "Trade Data" },
    { key: "DOCUMENTS", label: "Documents" },
    { key: "POST_IMPORT_ADJUSTMENT", label: "Post Import Adjustment" },
  ];
  const availablePageKeys = (tenantPagesResp?.pages || []);
  const filteredOptions = PAGE_OPTIONS.filter((o) => availablePageKeys.includes(o.key));

  const [showPermissions, setShowPermissions] = useState(false);

  const [permissions, setPermissions] = useState(clientInitialPermissions);

  const handleTriggerPermissions = () => {
    setShowPermissions((prev) => !prev);
    setPermissions(clientInitialPermissions);
  };

  // legacy handlers removed

  const handleProfileChange = (file: File | null) => {
    if (file) {
      const fileURL = URL.createObjectURL(file);
      setValue("profileImage", file);
      setProfilePreview(fileURL);
    }
  };

  const handleUpdateClient: SubmitHandler<ClientFormValues> = async (data) => {
    const { firstName, assignedPartner, profileImage, email } = data;

    let profileImgKey;
    if (profileImage) {
      const data = await uploadFile(profileImage);
      profileImgKey = data?.key || "";
    }
    const clientData: ClientType = {
      firstName,
      email,
      profileImage: profileImgKey || "",
      isRestricted: !showPermissions,
      assignedPartner: assignedPartner ? assignedPartner : null,
      clientPermission: {
        dashboard: Object.keys(permissions.dashboard).filter(
          (key) =>
            permissions.dashboard[key as keyof typeof permissions.dashboard]
        ),
        documents: permissions.documents,
        reports: Object.keys(permissions.reports).filter(
          (key) => permissions.reports[key as keyof typeof permissions.reports]
        ),
      } as ClientAccessLevelPermissions,
    };

    try {
      await updateClient({
        clientData,
        clientId: _id,
      }).unwrap();
      const finalPages = (allowedPages || []).filter((k) => availablePageKeys.includes(k));
      await setUserAllowedPages({ userId: _id, pages: finalPages }).unwrap();
      setSelectedPartner(null);
      showSnackBar("Client Updated Successfully!", "", "success");
      reset();
      setShow(false);
    } catch (error: any) {
      const errorMessage = error?.message;
      showSnackBar(errorMessage, "", "error");
    }
  };

  useEffect(() => {
    if (!profileImage) {
      setProfilePreview(preview_image);
      return;
    }

    (async () => {
      const { signedUrl } = await getAccessSignedUrl({
        fileName: profileImage,
      }).unwrap();

      setProfilePreview(signedUrl);
    })();
  }, [profileImage, getAccessSignedUrl]);

  useEffect(() => {
    setShowPermissions(!clientDetails.isRestricted);
    setAllowedPages(clientDetails.allowedPages || []);
  }, [clientDetails]);

  return (
    <ModalV2
      show={show}
      setShow={setShow}
      buttonAction={handleSubmit(handleUpdateClient)}
      isConfirmButtonLoading={isLoading || profileImgLoading}
      buttonText={"Update"}
      title="Update Client"
      subTitle="Update your client's details and manage their access settings in Riffs"
    >
      <Box component="form" onSubmit={handleSubmit(handleUpdateClient)}>
        <Stack my={2}>
          <Controller
            name="assignedPartner"
            control={control}
            render={({ field: { onChange, ...rest } }) => (
              <PartnerPicker
                {...rest}
                selectedPartner={selectedPartner}
                setSelectedPartner={(partner: SPOwnerPartnerTypes | null) => {
                  const partnerId = partner ? partner._id : "";
                  onChange(partnerId);
                  setSelectedPartner(partner);
                }}
                error={errors.assignedPartner}
              />
            )}
          />
          {errors.assignedPartner && (
            <Typography
              color="error"
              sx={{
                mt: 0.5,
                fontSize: "0.75rem",
                ml: 1,
              }}
            >
              {errors.assignedPartner.message}
            </Typography>
          )}
          <Divider sx={{ mt: 2 }} />
          <PictureUploader
            title="Logo"
            description="We recommend an image of at least 400x400"
            imgUrl={profilePreview}
            customPreviewType={imageType}
            onChange={handleProfileChange}
            isLoading={isFetchingImageUrl}
          />
          <Controller
            name="firstName"
            control={control}
            rules={{ required: "Client name is required" }}
            render={({ field: { onChange, ...rest }, fieldState }) => (
              <StyledTextInput
                label="Client Name"
                type="text"
                {...rest}
                placeholder="Client Name"
                handleChangeValue={onChange}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            rules={{
              required: "Email is required",
              pattern: {
                value: ValidationPatterns.email,
                message: "Invalid email address",
              },
            }}
            render={({ field: { onChange, ...rest }, fieldState }) => (
              <StyledTextInput
                label="Email Address"
                {...rest}
                type="text"
                disabled
                placeholder="h1@example.com"
                handleChangeValue={onChange}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />

          <Stack sx={{ border: "1px solid #e4e7ec" }}>
            <StyledBox
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <HeadingText>
                  Do you want your client to login to the system
                </HeadingText>

                <Typography
                  sx={{
                    color: "#8E9494",
                    fontSize: "12px",
                  }}
                >
                  This will provide your client a portal to login and manage
                  everything
                </Typography>
              </Box>
              <SwitchButton
                label=""
                checked={showPermissions}
                onChange={handleTriggerPermissions}
              />
            </StyledBox>
            <Divider />
            {showPermissions && (
              <>
                {/* Removed legacy Permission Level/Dashboard */}
                <Divider />
                {/* Removed legacy Documents Admin/View Only */}
                <Divider />
                {/* Removed legacy Reports */}
                <Divider />
                <StyledBox>
                  <HeadingText>Pages</HeadingText>
                  <Typography sx={{ color: "#8E9494", fontSize: "12px" }}>
                    Toggle which app pages this client can access
                  </Typography>
                </StyledBox>
                <StyledBox>
                  <MultiCheckbox
                    label="Core Pages"
                    checkboxes={filteredOptions.map((o)=>({ label: o.label }))}
                    checked={filteredOptions.map((o)=>allowedPages.includes(o.key))}
                    handleParentChange={(e)=>{
                      const checked = e.target.checked;
                      if (checked) setAllowedPages(filteredOptions.map((o)=>o.key));
                      else setAllowedPages([]);
                    }}
                    handleChildChange={(index)=>()=>{
                      const key = filteredOptions[index]?.key;
                      if (!key) return;
                      setAllowedPages(prev => prev.includes(key) ? prev.filter(k=>k!==key) : [...prev, key]);
                    }}
                  />
                </StyledBox>
              </>
            )}
          </Stack>
        </Stack>
      </Box>
    </ModalV2>
  );
};

export default UpdatePartnerModal;
